import { useState, useCallback, useRef, useEffect } from "react"
import { fetchUnits, createUnit, updateUnit, changeUnitStatus } from "../api/projectsApi"

// ── useUnits ─────────────────────────────────────────────────────────────────
// Fetches the units for a project (powers the availability grid). Supports an
// optional `pollMs` so the grid picks up changes made by other users without a
// websocket. This is the single seam where AnyCable could later be swapped in —
// a subscription would just call `refresh()`.
export function useUnits(projectId, { pollMs = 0 } = {}) {
  const [units, setUnits]     = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const abortRef              = useRef(null)
  const filtersRef            = useRef({})

  const fetch = useCallback(async (filters = {}) => {
    if (!projectId) return
    filtersRef.current = filters
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const result = await fetchUnits(projectId, filters)
      setUnits(result.units || [])
    } catch (err) {
      if (err.name !== "AbortError") setError(err)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  // Silent refresh — reuses the last filters, no spinner (for polling / post-mutation).
  const refresh = useCallback(async () => {
    if (!projectId) return
    try {
      const result = await fetchUnits(projectId, filtersRef.current)
      setUnits(result.units || [])
    } catch {
      // keep last good data on a transient poll failure
    }
  }, [projectId])

  // Light polling so multi-user blocks/unblocks surface within a few seconds.
  useEffect(() => {
    if (!pollMs || !projectId) return
    const t = setInterval(refresh, pollMs)
    return () => clearInterval(t)
  }, [pollMs, projectId, refresh])

  return { units, loading, error, fetch, refresh, setUnits }
}

// ── Mutations ──────────────────────────────────────────────────────────────
function useMutation(fn) {
  const [status, setStatus] = useState("idle")
  const [error, setError]   = useState(null)

  const mutate = useCallback(async (...args) => {
    setStatus("pending")
    setError(null)
    try {
      const result = await fn(...args)
      setStatus("success")
      return result
    } catch (err) {
      const msg = err.response?.data?.errors || [err.message || "Something went wrong"]
      setError(msg)
      setStatus("error")
      throw msg
    }
  }, [])

  return {
    mutate,
    status,
    error,
    isPending: status === "pending",
    reset: () => { setStatus("idle"); setError(null) },
  }
}

export const useCreateUnit = () =>
  useMutation((projectId, payload) => createUnit(projectId, payload))
export const useUpdateUnit = () =>
  useMutation((projectId, id, payload) => updateUnit(projectId, id, payload))
export const useChangeUnitStatus = () =>
  useMutation((projectId, id, body) => changeUnitStatus(projectId, id, body))
